import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { apiGet, apiPatch } from '../../../utils/api';

const STORAGE_KEY = 'jpro_notification_prefs';

const defaults = {
    // Team Chat
    teamChatToasts: true,
    teamChatSound: false,
    browserNotifications: true,
    // Announcements
    announcementBanner: true,
    // Tasks
    taskAssignedToast: true,
    taskAssignedEmail: true,
    taskAssignedSound: true,
    taskCompletedToast: true,
    taskCompletedEmail: true,
    taskStatusChangedToast: true,
    taskStatusChangedEmail: false,
    // Notes
    noteSharedToast: true,
    noteSharedEmail: true,
    noteSharedSound: true,
};

function loadPrefs() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? { ...defaults, ...JSON.parse(stored) } : { ...defaults };
    } catch {
        return { ...defaults };
    }
}

export function getNotificationPrefs() {
    return loadPrefs();
}

function getBrowserPermission() {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
}

function NotificationSettings() {
    const [prefs, setPrefs] = useState(loadPrefs);
    const [saved, setSaved] = useState(false);
    const [browserPerm, setBrowserPerm] = useState(getBrowserPermission);

    const requestBrowserPermission = async () => {
        if (typeof Notification === 'undefined') return;
        const result = await Notification.requestPermission();
        setBrowserPerm(result);
        if (result === 'granted') {
            toast.success('Browser notifications enabled');
        } else if (result === 'denied') {
            toast.error('Permission denied — you can re-enable in browser settings');
        }
    };

    const handleToggle = (key) => {
        setPrefs(prev => {
            const next = { ...prev, [key]: !prev[key] };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            return next;
        });
        setSaved(false);
    };

    const handleSave = async () => {
        // Save email prefs to server first
        try {
            const response = await apiPatch('/profile/notification_preferences', {
                task_assigned_email: prefs.taskAssignedEmail,
                task_completed_email: prefs.taskCompletedEmail,
                task_status_changed_email: prefs.taskStatusChangedEmail,
                note_shared_email: prefs.noteSharedEmail,
            });
            if (!response.ok) {
                setSaved(false);
                toast.error('Failed to save email notification preferences');
                return;
            }
        } catch {
            setSaved(false);
            toast.error('Failed to save email notification preferences');
            return;
        }

        // Only persist client-side prefs after server success
        localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
        setSaved(true);
        toast.success('Notification preferences saved');
        setTimeout(() => setSaved(false), 2000);
    };

    // Load server-side email prefs on mount
    useEffect(() => {
        const loadServerPrefs = async () => {
            try {
                const response = await apiGet('/profile/notification_preferences');
                if (response.ok) {
                    const data = await response.json();
                    const serverPrefs = data.preferences || {};
                    setPrefs(prev => ({
                        ...prev,
                        taskAssignedEmail: serverPrefs.task_assigned_email ?? prev.taskAssignedEmail,
                        taskCompletedEmail: serverPrefs.task_completed_email ?? prev.taskCompletedEmail,
                        taskStatusChangedEmail: serverPrefs.task_status_changed_email ?? prev.taskStatusChangedEmail,
                        noteSharedEmail: serverPrefs.note_shared_email ?? prev.noteSharedEmail,
                    }));
                }
            } catch { /* silent */ }
        };
        loadServerPrefs();
    }, []);

    const Toggle = ({ checked, onChange }) => (
        <button
            onClick={onChange}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                checked ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-600'
            }`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    checked ? 'translate-x-6' : 'translate-x-1'
                }`}
            />
        </button>
    );

    const SettingRow = ({ title, description, settingKey, extra }) => (
        <div className="flex items-center justify-between py-1">
            <div className="flex-1 min-w-0 mr-4">
                <h4 className="font-unna font-medium text-sm">{title}</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-rasa">{description}</p>
                {extra}
            </div>
            <Toggle checked={prefs[settingKey]} onChange={() => handleToggle(settingKey)} />
        </div>
    );

    const SectionHeader = ({ title }) => (
        <h3 className="text-xs font-rasa font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">{title}</h3>
    );

    const Divider = () => <div className="border-t border-gray-200 dark:border-gray-700 pt-5" />;

    return (
        <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-dm-serif font-semibold mb-6">Notification Preferences</h2>

                <div className="space-y-5">
                    {/* Tasks Section */}
                    <div>
                        <SectionHeader title="Tasks" />
                        <div className="space-y-4">
                            <SettingRow
                                title="Task Assigned — Popup"
                                description="Show a toast notification when someone assigns you a task"
                                settingKey="taskAssignedToast"
                            />
                            <SettingRow
                                title="Task Assigned — Email"
                                description="Receive an email when a task is assigned to you"
                                settingKey="taskAssignedEmail"
                            />
                            <SettingRow
                                title="Task Assigned — Sound"
                                description="Play a sound when a task is assigned to you"
                                settingKey="taskAssignedSound"
                            />
                            <SettingRow
                                title="Task Completed — Popup"
                                description="Get notified when someone completes a task you delegated"
                                settingKey="taskCompletedToast"
                            />
                            <SettingRow
                                title="Task Completed — Email"
                                description="Receive an email when a delegated task is completed"
                                settingKey="taskCompletedEmail"
                            />
                            <SettingRow
                                title="Task Status Changed — Popup"
                                description="Get notified when the status of a delegated task changes"
                                settingKey="taskStatusChangedToast"
                            />
                            <SettingRow
                                title="Task Status Changed — Email"
                                description="Receive an email when a delegated task's status changes"
                                settingKey="taskStatusChangedEmail"
                            />
                        </div>
                    </div>

                    <Divider />

                    {/* Notes Section */}
                    <div>
                        <SectionHeader title="Notes" />
                        <div className="space-y-4">
                            <SettingRow
                                title="Note Shared — Popup"
                                description="Show a toast notification when someone shares a note with you"
                                settingKey="noteSharedToast"
                            />
                            <SettingRow
                                title="Note Shared — Email"
                                description="Receive an email when a note is shared with you"
                                settingKey="noteSharedEmail"
                            />
                            <SettingRow
                                title="Note Shared — Sound"
                                description="Play a sound when a note is shared with you"
                                settingKey="noteSharedSound"
                            />
                        </div>
                    </div>

                    <Divider />

                    {/* Team Chat Section */}
                    <div>
                        <SectionHeader title="Team Chat" />
                        <div className="space-y-4">
                            <SettingRow
                                title="Message Notifications"
                                description="Show toast popups when new team chat messages arrive"
                                settingKey="teamChatToasts"
                            />
                            <SettingRow
                                title="Notification Sound"
                                description="Play a sound when new messages arrive"
                                settingKey="teamChatSound"
                            />
                            <div className="flex items-center justify-between py-1">
                                <div className="flex-1 min-w-0 mr-4">
                                    <h4 className="font-unna font-medium text-sm">Browser Push Notifications</h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 font-rasa">
                                        Get notified even when you're on a different tab or page
                                    </p>
                                    {browserPerm === 'denied' && (
                                        <p className="text-[10px] text-red-500 font-rasa mt-1">
                                            Permission blocked — re-enable in your browser's site settings
                                        </p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    {browserPerm === 'default' && (
                                        <button
                                            onClick={requestBrowserPermission}
                                            className="px-3 py-1 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-rasa"
                                        >
                                            Allow
                                        </button>
                                    )}
                                    {browserPerm === 'unsupported' ? (
                                        <span className="text-xs text-gray-400 font-rasa">Not supported</span>
                                    ) : (
                                        <Toggle
                                            checked={prefs.browserNotifications}
                                            onChange={() => {
                                                if (browserPerm === 'default') requestBrowserPermission();
                                                handleToggle('browserNotifications');
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <Divider />

                    {/* Announcements Section */}
                    <div>
                        <SectionHeader title="Announcements" />
                        <SettingRow
                            title="Announcement Banner"
                            description="Show announcement popups and banners"
                            settingKey="announcementBanner"
                        />
                    </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-rasa"
                    >
                        {saved ? 'Saved' : 'Save Preferences'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default NotificationSettings;
